import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import { validateLastRow } from "../../../../utils/validateTable";
import { restructureData } from "../../../../utils/restructureData";
import DynamicStaticTable from "../../../../Components/Common/DynamicStaticTable";
import CustomTextField from "../../GRI300/GRI308/CustomTextField";
import SectionHeading from "../../GRI300/GRI308/SectionHeading";

import ReviewBox from "../../../../Components/Common/ReviewBox";
import { currentYear } from "../../../../utils/generateYearRanges";

const GRI205_3 = ({ hide, downloadMode = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    confirmedIncidentsOfCorruption: "",
    corruptionDisciplinaryActions: "",
  });

  const [tables, setTables] = useState([]);
  const [tabErrors, setTabErros] = useState({});
  const [data, setData] = useState({});

  const inputData = [
    {
      fieldName: "corruptionTerminationsPartnerships",
      type: "array",
      // letter: "b.",
      fields: [
        {
          fieldName: "typeOfBusiness",
          title: "Type of Business Partner",
          type: "text",
          placeholder: "Enter Text",
          width: "44%",
        },

        {
          fieldName: "nonRenewalContracts",
          title:
            "Number of Incidents Leading to Termination or Non-Renewal of Contracts",
          type: "number",
          width: "44%",
          placeholder: "Enter Number",
        },
      ],
    },
  ];

  const defaultSegments = [
    "Suppliers",
    "Agents",
    "Lobbyists",
    "Intermediaries",
    "Joint Venture Partners",
    "Consortia Partners",
    "Governments",
    "Customers",
    "Clients",
    // "Others",
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI200&Id=${id}`);
        const data1 = response.data.data.GRI200 || {};
        if (response?.data?.data?.year < currentYear - 1) setDisabled(true);
        setData(data1);
        setFormData({
          confirmedIncidentsOfCorruption:
            data1.confirmedIncidentsOfCorruption || "",
          corruptionDisciplinaryActions:
            data1.corruptionDisciplinaryActions || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Update handler for existing rows

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate main form fields
    if (!formData.confirmedIncidentsOfCorruption) {
      newErrors.confirmedIncidentsOfCorruption = "This field is required";
      formIsValid = false;
    }

    if (!formData.corruptionDisciplinaryActions) {
      newErrors.corruptionDisciplinaryActions = "This field is required";
      formIsValid = false;
    }
    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // if (!validateForm()) return;

      if (
        tables?.[0]?.rows?.length > defaultSegments?.length &&
        !validateLastRow(inputData, tables, setTabErros)
      )
        return;
      const payload = {
        type: "GRI200",
        submitted205_3: true,
        confirmedIncidentsOfCorruption: formData.confirmedIncidentsOfCorruption,
        corruptionDisciplinaryActions: formData.corruptionDisciplinaryActions,
        ...restructureData(tables),
      };

      try {
        // Send PUT request to update the data
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/economic-performance/206-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/economic-performance/206-1/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {!hide && (
        <p className={styles.section_heading}>205 : Anti-corruption 2016</p>
      )}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        205-3 : Confirmed incidents of corruption and actions taken
      </p>

      {/* Section A */}

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section B */}
        <SectionHeading text="a. How many confirmed incidents of corruption occurred during the reporting period, and what was the nature of these incidents? What was the outcome of such cases?" />
        <CustomTextField
          value={formData.confirmedIncidentsOfCorruption}
          onChange={(e) =>
            handleInputChange("confirmedIncidentsOfCorruption", e.target.value)
          }
          error={errors.confirmedIncidentsOfCorruption}
          helperText={errors.confirmedIncidentsOfCorruption}
          disabled={disabled}
        />

        <SectionHeading text="b. How many confirmed incidents of corruption resulted in the dismissal or disciplinary action against employees?" />
        <CustomTextField
          value={formData.corruptionDisciplinaryActions}
          onChange={(e) =>
            handleInputChange("corruptionDisciplinaryActions", e.target.value)
          }
          error={errors.corruptionDisciplinaryActions}
          helperText={errors.corruptionDisciplinaryActions}
          disabled={disabled}
        />

        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <SectionHeading text="c. How many employees have the organization's anti-corruption policies and procedures been communicated to, and what percentage does this represent, broken down by employee category and region?" />
        <DynamicStaticTable
          {...{
            inputData,
            tables,
            setTables,
            errors: tabErrors,
            setErrors: setTabErros,
            data: data,
            defaultSegments,
            parameter: "typeOfBusiness",
            buttonHeading: "Add Field",
            disabled: disabled,
            hide,
          }}
        />

        <SectionHeading text="d. Report details of any public legal cases concerning corruption brought against the organization or its employees during the reporting period, including case status and outcomes." />
        <CustomTextField
          value={formData.publicLegalCasesCorruption}
          onChange={(e) =>
            handleInputChange("publicLegalCasesCorruption", e.target.value)
          }
          error={errors.publicLegalCasesCorruption}
          helperText={errors.publicLegalCasesCorruption}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI205_3;
